import type { Card } from './data/cards/types';
import type { HeroId, Hero } from './data/heroes';
import { generatePairings, type Pairing } from './pairing';
import { simulateCombat, type CombatResult } from './combatEngine';
import { runTavernAI } from './ai/tavernAI';
import { createAIPlayer, pickAIHeroes, generateOpponentBoard } from './ai';
import type { PlayerState } from './gameState';
import { mulberry32, type RNG } from './rng';
import { applyTribeEndOfTurn } from './tribeEndOfTurn';

// 8-player lobby orchestration for the v2 single-player loop.
//
// The match store is the canonical source of truth for the human's
// tavern state. This module owns the 7 AI opponents and the per-round
// tick that resolves pairings + applies damage + advances every
// survivor's tavern to the next round.
//
// The human is NOT modelled as an AIPlayer here — the store still owns
// their state and damage application. We expose deltas back to the
// store so it can apply them atomically alongside its own bookkeeping.

/**
 * AI opponent state. Mirrors v1 `PlayerState` minus the human-flagged
 * fields and the persistence noise (combatHistory, boardState, etc.).
 * Shape-compatible enough that `runTavernAI` accepts it after a cast.
 */
export interface AIPlayer {
  id: string;
  name: string;
  heroId: HeroId;
  hero: Hero;
  health: number;
  gold: number;
  tavernTier: number;
  turnsAtCurrentTier: number;
  board: (Card | null)[];
  hand: Card[];
  alive: boolean;
  heroPowerUsedThisTurn: boolean;
  refreshCountThisTurn: number;
}

/** A pairing's outcome, retained for the (future) scoreboard. */
export interface PairingOutcome {
  round: number;
  aId: string;
  bId: string;
  winner: 'a' | 'b' | 'draw';
  damage: number;
  /** Whether the human was a participant. */
  involvesHuman: boolean;
}

export interface RoundTickResult {
  /** Result of the human's pairing (for the canvas). */
  humanCombatResult: CombatResult;
  /** The AI the human faced this round (id). */
  humanOpponentId: string;
  /** AI-vs-AI fights resolved silently this round. */
  silentFights: PairingOutcome[];
  /** Total damage applied per player this tick, by id. */
  damageApplied: Record<string, number>;
  /** Ids that died this tick. */
  eliminated: string[];
  /** Round number AFTER this tick. */
  roundAfter: number;
  /** Updated opponents (new boards/gold/tier from their tavern). */
  opponentsAfter: AIPlayer[];
}

/**
 * Build the 7 AI opponents for a fresh lobby. The human is not
 * included — the store owns them.
 */
export function createLobby(
  humanHeroId: HeroId | null,
  _humanPicks: Card[],
  seed: number,
): { opponents: AIPlayer[]; rng: RNG } {
  const rng = mulberry32(seed);
  const heroes = pickAIHeroes(7, humanHeroId, rng);
  const opponents = heroes.map((hero, i) =>
    createAIPlayer(`P${i + 1}`, hero, rng),
  );
  return { opponents, rng };
}

/**
 * Snapshot of the human used as the second side of a pairing. The
 * store passes this in; we don't mutate it.
 */
export interface HumanSnapshot {
  id: string; // always 'P0'
  health: number;
  board: (Card | null)[];
  alive: boolean;
}

/**
 * Build a synthetic "PlayerState" adapter around an AIPlayer so the
 * existing v1 `runTavernAI` can mutate it. Only the fields tavernAI
 * touches are real — the rest are stubs (combatHistory, isHuman, etc.).
 */
function asPlayerState(ai: AIPlayer): PlayerState {
  return {
    id: ai.id,
    name: ai.name,
    isHuman: false,
    health: ai.health,
    gold: ai.gold,
    tavernTier: ai.tavernTier,
    turn: 0,
    board: ai.board,
    hand: ai.hand,
    hero: ai.hero,
    alive: ai.alive,
    heroPowerUsedThisTurn: ai.heroPowerUsedThisTurn,
    combatHistory: [],
    refreshCountThisTurn: ai.refreshCountThisTurn,
    boardState: 'editing',
    shopFrozen: false,
    frozenShopCards: [],
  };
}

/** Copy mutated fields back from the adapter into our AIPlayer. */
function syncFromPlayerState(ai: AIPlayer, ps: PlayerState): void {
  ai.health = ps.health;
  ai.gold = ps.gold;
  ai.tavernTier = ps.tavernTier;
  ai.board = ps.board;
  ai.hand = ps.hand;
  ai.alive = ps.alive;
  ai.heroPowerUsedThisTurn = ps.heroPowerUsedThisTurn;
  ai.refreshCountThisTurn = ps.refreshCountThisTurn;
}

/**
 * Standard auto-battler damage formula: floor(remaining-units-on-winner
 * * tier-weight + tier-of-winner). Match v1 behaviour by using the
 * `CombatResult.damage` field directly when it's populated; otherwise
 * fall back to a tier+units approximation.
 */
function damageFromResult(result: CombatResult, winnerTier: number, winnerUnitsAlive: number): number {
  if (typeof result.damage === 'number' && result.damage > 0) {
    return result.damage;
  }
  // Fallback (combatEngine should always set damage, but defend anyway).
  return Math.max(1, winnerTier + Math.floor(winnerUnitsAlive / 2));
}

/**
 * Resolve one full round: pair → fight → damage → advance.
 *
 * Pure-ish: it does NOT mutate the `opponents` array passed in. It
 * returns a new `opponentsAfter` snapshot for the caller (the store)
 * to commit, plus the deltas it needs to update the human.
 */
export function runRoundTick(
  human: HumanSnapshot,
  opponents: AIPlayer[],
  round: number,
  // RNG is reserved for future deterministic AI tavern routing. The
  // current path uses Math.random() in tavernAI/pairing for parity
  // with v1; threading the seeded RNG through is a follow-up.
  // Underscored to silence the unused-param TS lint.
  _rng: RNG,
  /**
   * If the caller has already resolved the human pairing (the store
   * does this against the v1 gameState singleton so hero powers
   * fire), pass it in to avoid re-simulating.
   */
  preComputedHumanResult?: { result: CombatResult; opponentId: string },
  /**
   * Pre-computed pairings for this round (computed in endTurn so that
   * `opponentBoard` and the tick agree on who faced whom).
   */
  presetPairings?: Pairing[],
): RoundTickResult {
  // Deep-clone opponents so we can mutate freely below.
  const next: AIPlayer[] = opponents.map((o) => ({
    ...o,
    board: [...o.board],
    hand: [...o.hand],
  }));

  // Build the alive-player list for pairing. The pairing helper expects
  // PlayerState[] — we adapt human + AIs to that shape transiently.
  const humanAdapter: PlayerState = {
    id: human.id,
    name: 'You',
    isHuman: true,
    health: human.health,
    gold: 0,
    tavernTier: 1,
    turn: round,
    board: human.board,
    hand: [],
    hero: null,
    alive: human.alive,
    heroPowerUsedThisTurn: false,
    combatHistory: [],
    refreshCountThisTurn: 0,
    boardState: 'editing',
    shopFrozen: false,
    frozenShopCards: [],
  };
  const aiAdapters: PlayerState[] = next.map(asPlayerState);
  const allForPairing: PlayerState[] = [humanAdapter, ...aiAdapters];

  // Use caller-supplied pairings if provided (the store pre-computes
  // them in endTurn so opponentBoard and the tick agree). Otherwise
  // fall back to fresh Fisher-Yates.
  // TODO(rng): thread our RNG into a v2-local pairing helper if we
  // ever want fully seeded replay (pairing currently uses Math.random).
  const pairings: Pairing[] = presetPairings ?? generatePairings(allForPairing, round);

  const damageApplied: Record<string, number> = {};
  const eliminated: string[] = [];
  const silentFights: PairingOutcome[] = [];
  let humanCombatResult: CombatResult | null = null;
  let humanOpponentId = human.id; // ghost fight default

  const findAI = (id: string): AIPlayer | undefined => next.find((p) => p.id === id);

  for (const pairing of pairings) {
    const isGhost = pairing.playerAId === pairing.playerBId;
    const aIsHuman = pairing.playerAId === human.id;
    const bIsHuman = pairing.playerBId === human.id;
    const involvesHuman = aIsHuman || bIsHuman;

    if (involvesHuman) {
      const oppId = aIsHuman ? pairing.playerBId : pairing.playerAId;
      humanOpponentId = isGhost ? human.id : oppId;
      let result: CombatResult;
      if (preComputedHumanResult) {
        result = preComputedHumanResult.result;
        // Trust the caller's opponentId if the pairing aligns. Fall
        // back to whatever pairing said.
        if (!isGhost) humanOpponentId = preComputedHumanResult.opponentId || oppId;
      } else {
        const oppBoard: Card[] = isGhost
          ? (human.board.filter(Boolean) as Card[])
          : ((findAI(oppId)?.board.filter(Boolean) as Card[]) ?? []);
        const enemyBoard: Card[] = oppBoard.length > 0 ? oppBoard : generateOpponentBoard(round, 1);
        result = simulateCombat(human.board, enemyBoard);
      }
      humanCombatResult = result;

      const winnerUnitsAlive = result.winner === 'player'
        ? (human.board.filter(Boolean) as Card[]).length
        : (findAI(humanOpponentId)?.board.filter(Boolean) as Card[] ?? []).length;
      const dmg = damageFromResult(result, 1, winnerUnitsAlive);
      if (result.winner === 'player' && !isGhost) {
        damageApplied[humanOpponentId] = (damageApplied[humanOpponentId] ?? 0) + dmg;
      } else if (result.winner === 'enemy') {
        damageApplied[human.id] = (damageApplied[human.id] ?? 0) + dmg;
      }
      continue;
    }

    // AI-vs-AI silent fight.
    const a = findAI(pairing.playerAId);
    const b = findAI(pairing.playerBId);
    if (!a) continue;
    if (isGhost) {
      // Lone-survivor ghost fight: treat as draw, zero damage.
      silentFights.push({
        round,
        aId: a.id,
        bId: a.id,
        winner: 'draw',
        damage: 0,
        involvesHuman: false,
      });
      continue;
    }
    if (!b) continue;
    const aBoard = a.board.filter(Boolean) as Card[];
    const bBoardArr = b.board.filter(Boolean) as Card[];
    // AI-vs-AI fight — the human's hero passives must not apply here.
    const result = simulateCombat(aBoard, bBoardArr, [], { heroPassives: false });
    const aTier = a.tavernTier;
    const bTier = b.tavernTier;
    const dmg = damageFromResult(
      result,
      result.winner === 'player' ? aTier : bTier,
      result.winner === 'player' ? aBoard.length : bBoardArr.length,
    );

    let winner: 'a' | 'b' | 'draw';
    if (result.winner === 'player') {
      winner = 'a';
      damageApplied[b.id] = (damageApplied[b.id] ?? 0) + dmg;
    } else if (result.winner === 'enemy') {
      winner = 'b';
      damageApplied[a.id] = (damageApplied[a.id] ?? 0) + dmg;
    } else {
      winner = 'draw';
    }
    silentFights.push({ round, aId: a.id, bId: b.id, winner, damage: result.damage ?? 0, involvesHuman: false });
  }

  // Apply damage and flag eliminations on AIs (human is handled in store).
  for (const ai of next) {
    const dmg = damageApplied[ai.id] ?? 0;
    if (dmg > 0) {
      ai.health = Math.max(0, ai.health - dmg);
      if (ai.health <= 0 && ai.alive) {
        ai.alive = false;
        eliminated.push(ai.id);
      }
    }
  }
  // Detect human elimination by the caller using damageApplied[human.id].

  const roundAfter = round + 1;

  // Advance each surviving AI's tavern phase for the *next* round.
  // runTavernAI mutates a PlayerState — we adapt, run, then sync back.
  for (const ai of next) {
    if (!ai.alive) continue;
    // Bump gold + turnsAtCurrentTier the way v1's advanceToNextRound
    // would, then let the tavern AI spend it.
    ai.turnsAtCurrentTier += 1;
    ai.gold = Math.min(10, roundAfter + 2); // BASE_GOLD_PER_TURN = 2 (mirror gameConfig)
    ai.heroPowerUsedThisTurn = false;
    ai.refreshCountThisTurn = 0;
    const adapter = asPlayerState(ai);
    runTavernAI(adapter, roundAfter, 'medium');
    syncFromPlayerState(ai, adapter);

    // Same tavern-phase tribe payoffs the human gets in endTurn (PLACA
    // tags, RAZA swarm growth). Without this the AIs would fall further
    // behind every round once tribe verbs started compounding.
    ai.board = applyTribeEndOfTurn(ai.board).board;
  }

  if (!humanCombatResult) {
    // Shouldn't happen — pairing always includes the human if alive.
    humanCombatResult = {
      winner: 'draw',
      damage: 0,
      events: [],
    };
  }

  return {
    humanCombatResult,
    humanOpponentId,
    silentFights,
    damageApplied,
    eliminated,
    roundAfter,
    opponentsAfter: next,
  };
}

/**
 * Compute the human's current opponent (by id) for the upcoming round.
 * Used by `endTurn` in the store to populate `opponentBoard` before the
 * canvas mounts. Side-effect-free.
 */
export function pickHumanOpponent(
  human: HumanSnapshot,
  opponents: AIPlayer[],
  round: number,
): { opponent: AIPlayer | null; pairings: Pairing[] } {
  const humanAdapter: PlayerState = {
    id: human.id,
    name: 'You',
    isHuman: true,
    health: human.health,
    gold: 0,
    tavernTier: 1,
    turn: round,
    board: human.board,
    hand: [],
    hero: null,
    alive: human.alive,
    heroPowerUsedThisTurn: false,
    combatHistory: [],
    refreshCountThisTurn: 0,
    boardState: 'editing',
    shopFrozen: false,
    frozenShopCards: [],
  };
  const aiAdapters = opponents.map(asPlayerState);
  const pairings = generatePairings([humanAdapter, ...aiAdapters], round);
  const myPairing = pairings.find(
    (p) => p.playerAId === human.id || p.playerBId === human.id,
  );
  if (!myPairing) return { opponent: null, pairings };
  if (myPairing.playerAId === myPairing.playerBId) {
    // Ghost — face self.
    return { opponent: null, pairings };
  }
  const oppId = myPairing.playerAId === human.id ? myPairing.playerBId : myPairing.playerAId;
  return { opponent: opponents.find((o) => o.id === oppId) ?? null, pairings };
}
